import {
  LookupStructuredTableMetadata,
  LookupStructuredTableRowMetadata,
} from 'notebook/integrations/lookup/LookupStructuredTableAdapter';
import makeSimpleActionFunction, {
  ActionParamCallback,
  getSubmitButtonLabel,
  getSubmitButtonStyle,
} from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import invokeStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/invokeStructuredTableMappings';
import {
  StructuredTableMappingsGlobalState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import isTableActionButtonDisabled from 'notebook/structured-tables/util/isTableActionButtonDisabled';
import tableActionButtonDisabledTooltip from 'notebook/structured-tables/util/tableActionButtonDisabledTooltip';
import {ButtonStyle} from 'ui-library/buttons/Button';

const onInvokeLookupTableSubmit: ActionParamCallback<
  LookupStructuredTableMetadata,
  LookupStructuredTableRowMetadata,
  void
> = ({entryOrTemplateId, structuredTable, updateHandler}) => {
  if (!entryOrTemplateId) {
    return;
  }
  updateHandler({
    // the only thing we're changing is making all rows finalized
    updatedRowMetadatas: structuredTable.rows.map((row): LookupStructuredTableRowMetadata => ({
      ...row.metadata,
      isFinalized: true,
    })),
    isFinalizedByRow: Array(structuredTable.rows.length).fill(true),
    // all values below do not change
    columnUpdates: {},
    updatedCells: structuredTable.rows.map((row) => row.cells),
  });
  invokeStructuredTableMappings(entryOrTemplateId, structuredTable);
};

/**
 * Lookup Table Submission is only used for Table Mapping.
 *
 * When a Lookup Table row is submitted, it finalizes its rows and then performs Table Mapping.
 */
export const submitLookupTableActionFunction = makeSimpleActionFunction<
  LookupStructuredTableMetadata,
  LookupStructuredTableRowMetadata
>({
  label: (params) => getSubmitButtonLabel(params),
  icon: 'column-mapping',
  buttonDesign: {
    buttonStyle: (params) => getSubmitButtonStyle(params, ButtonStyle.SECONDARY),
    showTextOnButton: true,
  },
  getDisabled: (params) => isTableActionButtonDisabled(params),
  getTooltip: tableActionButtonDisabledTooltip,
  getHidden: ({entryOrTemplateId, structuredTable}) => {
    if (!entryOrTemplateId) {
      return true;
    }
    return !StructuredTableMappingsStateManager.hasMappingsFromSourceTable(
      StructuredTableMappingsGlobalState.getState(entryOrTemplateId),
      structuredTable.provenanceUuid
    );
  },
  onInvoke: onInvokeLookupTableSubmit,
});
